import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function DiPage(): Node {
  return html`
    <div>
      <h1>Dependency Injection</h1>
      <p>onefold provides a lightweight DI system with <code>createToken</code>, <code>provide</code>, <code>inject</code>, and <code>runWithProviders</code> for testable, decoupled architecture.</p>

      <h2>createToken</h2>
      ${CodeBlock(`import { createToken } from 'onefold';

// Define typed injection tokens
const HttpToken = createToken<HttpClient>('HttpClient');
const AuthToken = createToken<AuthService>('AuthService');
const LoggerToken = createToken<Logger>('Logger');`)}

      <h2>provide / inject</h2>
      ${CodeBlock(`import { provide, inject } from 'onefold';

// Provide implementations
provide(HttpToken, new HttpClient({ baseUrl: '/api' }));
provide(AuthToken, new AuthService());

// Inject in any component
function UserList(): Node {
  const http = inject(HttpToken);
  const auth = inject(AuthToken);

  // Use the injected services
  return html\`<p>Logged in: \${auth.isAuthenticated()}</p>\`;
}`)}

      <h2>runWithProviders</h2>
      <p>Scope providers to a specific subtree (useful for testing or isolation):</p>
      ${CodeBlock(`import { runWithProviders } from 'onefold';

// Production
runWithProviders([
  [HttpToken, new HttpClient({ baseUrl: '/api' })],
  [AuthToken, new AuthService()],
  [LoggerToken, new ConsoleLogger()],
], () => {
  mount(App(), document.getElementById('app')!);
});

// Testing — swap implementations
runWithProviders([
  [HttpToken, new MockHttpClient()],
  [AuthToken, new MockAuthService()],
  [LoggerToken, new NoopLogger()],
], () => {
  // App uses mocks — no network calls
  mount(App(), container);
});`)}

      ${Callout('DI makes your components testable without modifying their source. Swap the real HTTP client for a mock in tests.')}

      <h2>API</h2>
      <table>
        <tr><th>Function</th><th>Parameters</th><th>Description</th></tr>
        <tr><td><code>createToken</code></td><td>(name: string)</td><td>Create a typed injection token.</td></tr>
        <tr><td><code>provide</code></td><td>(token, value)</td><td>Register a value for a token.</td></tr>
        <tr><td><code>inject</code></td><td>(token)</td><td>Retrieve the value for a token.</td></tr>
        <tr><td><code>runWithProviders</code></td><td>(providers[], fn)</td><td>Run a function with scoped providers.</td></tr>
      </table>

      ${Playground(`import { createSignal, html, mount } from 'onefold';

function App() {
  // Simple DI container
  const container = {};

  function provide(key, value) {
    container[key] = value;
  }

  function inject(key) {
    return container[key];
  }

  // Provide a UserService
  provide('UserService', {
    name: 'Alice',
    role: 'Admin',
    getGreeting() { return 'Hello, ' + this.name + '!'; }
  });

  provide('Logger', {
    log(msg) { return '[LOG] ' + msg; }
  });

  // Inject and use
  const userService = inject('UserService');
  const logger = inject('Logger');

  const output = createSignal(userService.getGreeting());

  function refresh() {
    const svc = inject('UserService');
    const log = inject('Logger');
    output.set(log.log(svc.getGreeting() + ' Role: ' + svc.role));
  }

  return html\`
    <div>
      <h3>Dependency Injection</h3>
      <p>Injected UserService and Logger from container:</p>
      <div style="padding:12px;background:#f8fafc;border-radius:6px;border:1px solid #e5e7eb;margin:12px 0">
        <code>\${() => output()}</code>
      </div>
      <button onclick=\${refresh}>Call via Logger</button>
      <p style="font-size:12px;color:#666;margin-top:8px">Services are resolved from the DI container at runtime.</p>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'Provide / Inject Demo')}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/security/guards">RBAC Guards</a> — role-based access control for routes</li>
        <li><a href="/state/store">Store</a> — manage structured application state</li>
      </ul>
    </div>
  `;
}
